/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { SYSTEM_COLLAPSE, SYSTEM_EXPAND, USER_COLLAPSE, USER_EXPAND } from 'common/actions/row-groups/types';
import {
  CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY,
  FIRM_LIQUIDATION_PREFERENCE_ALIAS,
  FIRM_OWNERSHIP_PERCENTAGE_ALIAS,
  FIRM_SHARES_ALIAS,
  INITIAL_CAP_TABLE_STATE,
  PREFERRED_TERMS_ALIAS,
  STRIKE_PRICE_ALIAS,
} from 'common/constants/cap-table';
import useLocalStorageByUser from 'common/hooks/useLocalStorageByUser';
import { rowsGroupsReducer } from 'common/reducers/rowsGroups';
import { RowsGroupsAction } from 'common/reducers/rowsGroups/types';

/**
 * Custom hook to manage Cap Table row groups state with persistence to localStorage.
 * Handles saving/restoring user preferences for row group expand/collapse state.
 * 
 * @returns {Object} Object containing rowGroups state and setRowGroups function
 */
export const useCapTableRowGroups = () => {
  // Load saved row group preferences
  const [savedPreferences, setSavedPreferences] = useLocalStorageByUser(
    CAP_TABLE_ROW_GROUPS_PREFERENCES_KEY,
    {}
  );

  // Dynamically added rows that should start collapsed by default (same as strike_price and prefered_terms)
  const dynamicallyAddedRows = useMemo(
    () => [FIRM_SHARES_ALIAS, FIRM_OWNERSHIP_PERCENTAGE_ALIAS, FIRM_LIQUIDATION_PREFERENCE_ALIAS],
    []
  );

  // Initialize row groups with saved preferences merged with defaults
  const initialStateWithPreferences = useMemo(() => {
    const defaultState = INITIAL_CAP_TABLE_STATE;
    const mergedState: Record<string, string> = { ...defaultState };
    
    // Merge saved preferences for all row groups (not just those in INITIAL_CAP_TABLE_STATE)
    // This ensures dynamically added rows (like fund ownership) get their saved preferences
    if (savedPreferences && typeof savedPreferences === 'object' && Object.keys(savedPreferences).length > 0) {
      Object.keys(savedPreferences).forEach(key => {
        const savedValue = savedPreferences[key];
        if (savedValue === USER_EXPAND || savedValue === USER_COLLAPSE) {
          mergedState[key] = savedValue;
        }
      });
      // Also ensure default state keys are preserved if not in saved preferences
      Object.keys(defaultState).forEach(key => {
        if (!(key in savedPreferences)) {
          mergedState[key] = defaultState[key as keyof typeof defaultState];
        }
      });
    } else {
      // If localStorage is empty, initialize dynamically added rows with SYSTEM_COLLAPSE
      // This ensures they start collapsed (same as strike_price and prefered_terms) and icon displays correctly
      dynamicallyAddedRows.forEach(key => {
        if (!(key in mergedState)) {
          mergedState[key] = SYSTEM_COLLAPSE;
        }
      });
    }
    return mergedState;
  }, [savedPreferences]);

  const [rowGroups, setRowGroupsBase] = useReducer(rowsGroupsReducer, initialStateWithPreferences);
  const previousRowGroupsRef = useRef(initialStateWithPreferences);
  const isInitialMountRef = useRef(true);
  const setSavedPreferencesRef = useRef(setSavedPreferences);
  const savedPreferencesLoadedRef = useRef(false);
  // Store savedPreferences in a ref so wrapper can access it even if it loads late
  const savedPreferencesRef = useRef(savedPreferences);
  
  // Update ref when savedPreferences changes
  useEffect(() => {
    savedPreferencesRef.current = savedPreferences;
  }, [savedPreferences]);
  
  // Update ref when setter changes
  useEffect(() => {
    setSavedPreferencesRef.current = setSavedPreferences;
  }, [setSavedPreferences]);

  // Track when we're restoring a saved preference (to avoid saving it as a new change)
  const isRestoringPreferenceRef = useRef(false);
  
  // Handle case where savedPreferences loads after initial mount
  // This can happen if user data loads asynchronously
  useEffect(() => {
    if (savedPreferences && typeof savedPreferences === 'object' && Object.keys(savedPreferences).length > 0) {
      if (!savedPreferencesLoadedRef.current) {
        // First time savedPreferences loads - apply them to state if they differ
        savedPreferencesLoadedRef.current = true;
        const defaultStateKeys = Object.keys(INITIAL_CAP_TABLE_STATE);
        const updates: Array<{ type: string; row: string }> = [];
        
        Object.keys(savedPreferences).forEach(key => {
          const savedValue = savedPreferences[key];
          if (savedValue === USER_EXPAND || savedValue === USER_COLLAPSE) {
            const currentValue = rowGroups[key];
            // For rows in INITIAL_CAP_TABLE_STATE, always apply saved preference if different
            // For other rows, only apply if current is system action or undefined
            const shouldUpdate = defaultStateKeys.includes(key)
              ? currentValue !== savedValue
              : (currentValue !== savedValue && (currentValue === SYSTEM_EXPAND || currentValue === SYSTEM_COLLAPSE || !currentValue));
            
            if (shouldUpdate) {
              updates.push({ type: savedValue, row: key });
            }
          }
        });
        
        if (updates.length > 0) {
          isRestoringPreferenceRef.current = true;
          updates.forEach(({ type, row }) => {
            setRowGroupsBase({
              type: type as typeof USER_EXPAND | typeof USER_COLLAPSE,
              row,
            });
          });
          setTimeout(() => {
            isRestoringPreferenceRef.current = false;
          }, 0);
        }
      }
    }
  }, [savedPreferences, rowGroups]);
  
  // Store rowGroups in a ref so wrapper can access latest value
  const rowGroupsRef = useRef(rowGroups);
  
  // Update ref when rowGroups changes
  useEffect(() => {
    rowGroupsRef.current = rowGroups;
  }, [rowGroups]);
  
  // Helper function to check and restore saved preference
  const restoreSavedPreference = useCallback(
    (row: string, currentSavedPreferences: Record<string, string> | null | undefined): boolean => {
      if (!currentSavedPreferences || typeof currentSavedPreferences !== 'object' || !(row in currentSavedPreferences)) {
        return false;
      }
      
      const savedValue = currentSavedPreferences[row];
      if (savedValue !== USER_EXPAND && savedValue !== USER_COLLAPSE) {
        return false;
      }
      
      // Mark that we're restoring a preference to prevent saving it as a new change
      isRestoringPreferenceRef.current = true;
      // Respect the user's saved preference instead of system action
      setRowGroupsBase({
        type: savedValue,
        row,
      });
      // Reset flag after a brief delay to allow state update
      setTimeout(() => {
        isRestoringPreferenceRef.current = false;
      }, 0);
      return true;
    },
    []
  );

  // Helper function to check if current state is a user preference
  const isUserPreference = (state: string | undefined): boolean => 
    state === USER_EXPAND || state === USER_COLLAPSE;

  // Helper function to check if row should preserve collapsed state
  const shouldPreserveCollapsedState = (
    row: string,
    currentState: string | undefined,
    currentSavedPreferences: Record<string, string> | null | undefined
  ): boolean => {
    if (currentState !== SYSTEM_COLLAPSE) {
      return false;
    }
    
    // Check if this is a row that should have preferences (strike_price, prefered_terms, or any saved row)
    const isPreferenceRow = row === STRIKE_PRICE_ALIAS || row === PREFERRED_TERMS_ALIAS ||
      (currentSavedPreferences && typeof currentSavedPreferences === 'object' && row in currentSavedPreferences);
    
    return Boolean(isPreferenceRow);
  };

  // Helper function to check if SYSTEM_EXPAND should be prevented for a row without saved preference
  const shouldPreventSystemExpandForDefaultCollapsedRow = (
    row: string,
    currentSavedPreferences: Record<string, string> | null | undefined
  ): boolean => {
    // If this is a dynamically added row and there's no saved preference, prevent SYSTEM_EXPAND
    // This ensures they stay collapsed (default) unless user explicitly expanded them
    if (dynamicallyAddedRows.includes(row)) {
      const hasSavedPreference = currentSavedPreferences && typeof currentSavedPreferences === 'object' && row in currentSavedPreferences;
      return !hasSavedPreference;
    }
    
    return false;
  };

  // Helper function to handle SYSTEM_EXPAND for dynamically added rows without saved preferences
  const handleSystemExpandForDefaultCollapsedRow = useCallback(
    (row: string, currentState: string | undefined, currentSavedPreferences: Record<string, string> | null | undefined): boolean => {
      if (!shouldPreventSystemExpandForDefaultCollapsedRow(row, currentSavedPreferences)) {
        return false;
      }

      // If current state is already SYSTEM_COLLAPSE, prevent the expansion
      if (currentState === SYSTEM_COLLAPSE) {
        return true; // Prevent the action
      }

      // If current state is SYSTEM_EXPAND but there's no saved preference, collapse it
      // This handles the case where the row was expanded but should default to collapsed
      if (currentState === SYSTEM_EXPAND) {
        isRestoringPreferenceRef.current = true;
        setRowGroupsBase({
          type: SYSTEM_COLLAPSE,
          row,
        });
        setTimeout(() => {
          isRestoringPreferenceRef.current = false;
        }, 0);
        return true; // Prevent the original action
      }

      // For any other state, prevent the expansion
      return true;
    },
    []
  );

  // Wrap setRowGroups to respect saved preferences and prevent system actions from overwriting user preferences
  const setRowGroups = useCallback(
    (action: RowsGroupsAction) => {
      // If this is a SYSTEM_EXPAND or SYSTEM_COLLAPSE action, check if there's a saved user preference
      // and respect it instead of overwriting
      if (action.type === SYSTEM_EXPAND || action.type === SYSTEM_COLLAPSE) {
        // Check saved preferences using ref to get latest value even if it loaded after mount
        const currentSavedPreferences = savedPreferencesRef.current;
        // Use ref to get latest rowGroups state
        const currentState = rowGroupsRef.current[action.row];
        
        // First, check if there's a saved preference and restore it
        if (restoreSavedPreference(action.row, currentSavedPreferences)) {
          return;
        }
        
        // Also check if current state is already a user preference - preserve it
        // This handles cases where savedPreferences hasn't loaded yet but user has toggled
        if (isUserPreference(currentState)) {
          // Don't allow system action to overwrite existing user preference
          return;
        }
        
        // If SYSTEM_EXPAND is being dispatched but current state is SYSTEM_COLLAPSE,
        // and this is a row that should respect user preferences (strike_price, prefered_terms),
        // preserve the collapsed state to prevent SYSTEM_EXPAND from expanding collapsed rows during save
        if (action.type === SYSTEM_EXPAND && shouldPreserveCollapsedState(action.row, currentState, currentSavedPreferences)) {
          // Preserve the collapsed state - don't allow SYSTEM_EXPAND to expand it
          return;
        }
        
        // If SYSTEM_EXPAND is being dispatched for a dynamically added row without saved preference,
        // prevent it to ensure the row stays collapsed (default state) unless user explicitly expanded it
        if (action.type === SYSTEM_EXPAND && handleSystemExpandForDefaultCollapsedRow(action.row, currentState, currentSavedPreferences)) {
          return;
        }
        
        // If no saved preference exists and current state is not a user preference, allow the system action to proceed
      }
      isRestoringPreferenceRef.current = false;
      setRowGroupsBase(action);
    },
    [restoreSavedPreference, handleSystemExpandForDefaultCollapsedRow] // Include helper functions in dependencies
  );
  
  // Proactively initialize dynamically added rows when localStorage is empty
  // This ensures the icon displays correctly even before the row is added to rowGroups by the spreadsheet
  // Run this effect whenever rowGroups changes to catch newly added rows immediately
  useEffect(() => {
    const defaultStateKeys = Object.keys(INITIAL_CAP_TABLE_STATE);
    
    // Check each dynamically added row
    dynamicallyAddedRows.forEach(key => {
      // Skip rows in INITIAL_CAP_TABLE_STATE - they're handled in initial state
      if (defaultStateKeys.includes(key)) {
        return;
      }
      
      const currentValue = rowGroups[key];
      // Check if there's a saved preference
      const hasSavedPreference = savedPreferences && typeof savedPreferences === 'object' && key in savedPreferences;
      
      // If row is not in state and there's no saved preference, initialize it with SYSTEM_COLLAPSE
      // This ensures they start collapsed (same as strike_price and prefered_terms) and icon shows correctly
      if (!currentValue && !hasSavedPreference) {
        isRestoringPreferenceRef.current = true;
        setRowGroupsBase({
          type: SYSTEM_COLLAPSE,
          row: key,
        });
        setTimeout(() => {
          isRestoringPreferenceRef.current = false;
        }, 0);
      }
    });
  }, [savedPreferences, rowGroups, dynamicallyAddedRows]);

  // Initialize dynamically added row groups with saved preferences when they first appear
  // This handles cases where row groups are added after initial mount (like fund ownership rows)
  // Note: Rows in INITIAL_CAP_TABLE_STATE (strike_price, prefered_terms) are already handled in initial state
  useEffect(() => {
    // Only run after initial mount to avoid interfering with initial state
    if (!isInitialMountRef.current && savedPreferences && typeof savedPreferences === 'object') {
      const defaultStateKeys = Object.keys(INITIAL_CAP_TABLE_STATE);
      // Check for any row groups in saved preferences that aren't in INITIAL_CAP_TABLE_STATE
      // (those are handled in initial state) and aren't in current state or have system actions
      Object.keys(savedPreferences).forEach(key => {
        // Skip rows that are in INITIAL_CAP_TABLE_STATE - they're already in initial state
        if (defaultStateKeys.includes(key)) {
          return;
        }
        const savedValue = savedPreferences[key];
        if (savedValue === USER_EXPAND || savedValue === USER_COLLAPSE) {
          const currentValue = rowGroups[key];
          // If row group doesn't exist yet, or exists with a system action, apply saved preference
          // But don't override if it's already the correct user preference
          if (!currentValue || (currentValue === SYSTEM_EXPAND || currentValue === SYSTEM_COLLAPSE)) {
            if (currentValue !== savedValue) {
              isRestoringPreferenceRef.current = true;
              setRowGroupsBase({
                type: savedValue,
                row: key,
              });
              setTimeout(() => {
                isRestoringPreferenceRef.current = false;
              }, 0);
            }
          }
        }
      });
    }
  }, [savedPreferences, rowGroups]);

  // Save preferences when rowGroups state changes to USER_EXPAND or USER_COLLAPSE
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousRowGroupsRef.current = { ...rowGroups };
      return;
    }

    // Check if any row group changed to USER_EXPAND or USER_COLLAPSE
    // Only save if it was a user-initiated change (not a system action that was prevented)
    const userPreferences: Record<string, string> = {};
    let hasUserPreferenceChange = false;

    Object.keys(rowGroups).forEach(key => {
      const currentValue = rowGroups[key];
      const previousValue = previousRowGroupsRef.current[key];
      const existingSavedValue = savedPreferences?.[key];

      // Initial checks: only proceed if current is a user preference, it changed, and we're not restoring
      if (
        (currentValue === USER_EXPAND || currentValue === USER_COLLAPSE) &&
        currentValue !== previousValue &&
        !isRestoringPreferenceRef.current
      ) {
        const wasUserPreference = previousValue === USER_EXPAND || previousValue === USER_COLLAPSE;
        const wasSystemAction = previousValue === SYSTEM_EXPAND || previousValue === SYSTEM_COLLAPSE || !previousValue;
        const isRestoringExistingPreference = existingSavedValue === currentValue && 
          (previousValue === SYSTEM_EXPAND || previousValue === SYSTEM_COLLAPSE || !previousValue);
        
        // Never save if current value matches existing saved preference
        // This prevents overwriting saved preferences when restoring or when system actions are prevented
        if (existingSavedValue === currentValue) {
          // Current value already matches saved preference - don't save (no change needed)
          return; // Skip to next key
        }
        
        // Save if this was a user-initiated toggle:
        // - User toggled from system state to user preference (wasSystemAction -> user preference)
        //   This handles the first toggle when localStorage is empty
        // - OR user toggled between user preferences (wasUserPreference -> different user preference)
        //   This handles subsequent toggles
        // - AND the new value is different from what's already saved
        // - AND we're not just restoring an existing saved preference
        if (
          (wasSystemAction || wasUserPreference) && 
          currentValue !== existingSavedValue && 
          !isRestoringExistingPreference
        ) {
          userPreferences[key] = currentValue;
          hasUserPreferenceChange = true;
        }
      }
    });

    // Only save if there was an actual user-initiated change that differs from saved preferences
    // Don't save if we're just restoring a saved preference (to avoid overwriting with system actions)
    if (hasUserPreferenceChange && !isRestoringPreferenceRef.current) {
      // Always preserve ALL existing saved preferences, only update the ones that changed
      const existingPreferences = savedPreferences && typeof savedPreferences === 'object' 
        ? { ...savedPreferences } 
        : {};
      const preferencesToSave = { ...existingPreferences };
      
      // Only update keys that actually changed due to user action
      Object.keys(userPreferences).forEach(key => {
        preferencesToSave[key] = userPreferences[key];
      });
      
      // Use ref to avoid dependency issues and prevent infinite loops
      // Use requestAnimationFrame to defer the save and avoid synchronous updates
      requestAnimationFrame(() => {
        setSavedPreferencesRef.current(preferencesToSave);
      });
    }

    previousRowGroupsRef.current = { ...rowGroups };
  }, [rowGroups, savedPreferences]); // savedPreferences included to check against existing saved values

  return {
    rowGroups,
    setRowGroups,
  };
};
